import React from 'react';
import { Link } from 'react-router-dom';

// Presentational Object that supports local #anchors (within this file) via normal anchor tags, <a href=, and ref= attributes
// Notes:
//  o Use < Link component for links within the docs, but outside of this file.
//  o Change class= to className= and style values to their JavaScript attribute equivalent (Ex: min-width => minWidth)
class Reference extends React.PureComponent {

	componentDidMount() {
		if (window.Prism) { window.Prism.highlightAll() }
	}

	componentDidUpdate() {
		if (window.Prism) { window.Prism.highlightAll() }
	}

	// Component isn't based on state or props so don't render it multiple times
	shouldComponentUpdate() {
		return false;
	}

	render() {
		return (
			<div className="javascript-sdk-reference">
				<h1>Functional Reference</h1>
				<h2 id="reference-changePassword">VoiceStorm.changePassword()</h2>
				<p>Allows the currently logged in user to change his password. Returns a promise object. The success handler will be passed a <Link to="/api/Responses/successResponse">successResponse</Link>.</p>
				<div className='data-table-responsive'>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.changePassword(options)</strong></td>
							</tr>
							<tr>
								<td colSpan="3">options is a set of key/value pairs to configure the request:</td>
							</tr>
							<tr>
								<td>currentPassword</td>
								<td>string</td>
								<td>required value of user's current password</td>
							</tr>
							<tr>
								<td>newPassword</td>
								<td>string</td>
								<td>required new value for user's password</td>
							</tr>
						</tbody>
					</table>
				</div>
				<h2 id="reference-currentUser">VoiceStorm.currentUser()</h2>
				<p>Requests the currently logged in user. Returns a promise object. The success handler will be passed a <Link to="/api/Users#get-user">GET /v1/user</Link> response. If the user is not currently logged on, the error handler will be called.</p>
				<div className='data-table-responsive'>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.currentUser()</strong></td>
							</tr>
						</tbody>
					</table>
				</div>
				<h2 id="reference-geos">VoiceStorm.geos()</h2>
				<p>Requests the list of defined countries, provinces, and states. Returns a promise object. The success handler will be passed a <Link to="/api/Utility#get-utility-geos">GET /v1/utility/geos</Link> response.</p>
				<div className='data-table-responsive'>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.geos()</strong></td>
							</tr>
						</tbody>
					</table>
				</div>
				<h2 id="reference-leaderboard">VoiceStorm.leaderboard()</h2>
				<p>Requests the details of a specific leaderboard. Returns a promise object. The success handler will be passed a <Link to="/api/Leaderboards#get-leaderboard-id">GET /v1/leaderboard</Link> response.</p>
				<div className='data-table-responsive'>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.leaderboard(id)</strong></td>
							</tr>
							<tr>
								<td>id</td>
								<td>number</td>
								<td>required id of the leaderboard to request</td>
							</tr>
						</tbody>
					</table>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.leaderboard(options)</strong></td>
							</tr>
							<tr>
								<td colSpan="3">options is a set of key/value pairs to configure the request:</td>
							</tr>
							<tr>
								<td>id</td>
								<td>number</td>
								<td>required id of the leaderboard to request</td>
							</tr>
						</tbody>
					</table>
				</div>
				<h2 id="reference-leaderboardRankings">VoiceStorm.leaderboardRankings()</h2>
				<p>Requests the rankings of a specific leaderboard. Returns a promise object. The success handler will be passed a <Link to="/api/Leaderboards#get-leaderboard-id-rankings">{'GET /v1/leaderboard/{id}/rankings'}</Link> response.</p>
				<div className='data-table-responsive'>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.leaderboardRankings(id)</strong></td>
							</tr>
							<tr>
								<td>id</td>
								<td>number</td>
								<td>required id of the leaderboard to request</td>
							</tr>
						</tbody>
					</table>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.leaderboardRankings(options)</strong></td>
							</tr>
							<tr>
								<td colSpan="3">options is a set of key/value pairs to configure the request:</td>
							</tr>
							<tr>
								<td>id</td>
								<td>number</td>
								<td>required id of the leaderboard to request</td>
							</tr>
							<tr>
								<td>skip</td>
								<td>number</td>
								<td>optional start index of rankings in leaderboard</td>
							</tr>
							<tr>
								<td>take</td>
								<td>number</td>
								<td>optional count of rankings to return</td>
							</tr>
							<tr>
								<td>include</td>
								<td>string</td>
								<td>optional comma delimited string of the following to request information:<br />
									<ul>
										<li>&lsquo;images&rsquo; to return images with the leaderboard</li>
									</ul>
								</td>
							</tr>
							<tr>
								<td>sortMode</td>
								<td>string</td>
								<td>optional one of the following to sort the rankings:<br />
									<ul>
										<li>'Rank' to sort by rank</li>
										<li>'JoinDateDescending' to sort by user join date</li>
										<li>'AlphabeticalByUserName' to sort by user name</li>
									</ul>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<h2 id="reference-login">VoiceStorm.login()</h2>
				<p>Logs a user in. The VoiceStorm SDK will persist the logged in user's session until it expires or <a href="#reference-logout">VoiceStorm.logout()</a> is called. Returns a promise object. The success handler will be passed a <Link to="/api/Users#get-user">GET /v1/user</Link> response representing the logged in user.</p>
				<div className='data-table-responsive'>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.login(email, password)</strong></td>
							</tr>
							<tr>
								<td>email</td>
								<td>string</td>
								<td>required email address of user logging in</td>
							</tr>
							<tr>
								<td>password</td>
								<td>string</td>
								<td>required password of user logging in'[']</td>
							</tr>
						</tbody>
					</table>
				</div>
				<h2 id="reference-loginWithToken">VoiceStorm.loginWithToken()</h2>
				<p>Logs a user in with token and expiration from a <Link to="/api/Login#post-login">POST /v1/login</Link> response. The VoiceStorm SDK will persist the logged in user's session until it expires or <a href="#reference-logout">VoiceStorm.logout()</a> is called. Returns a promise object. The success handler will be passed a <Link to="/api/Users#get-user">GET /v1/user</Link> response representing the logged in user.</p>
				<div className='data-table-responsive'>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.loginWithToken(token, expiration)</strong></td>
							</tr>
							<tr>
								<td>token</td>
								<td>string</td>
								<td>required token</td>
							</tr>
							<tr>
								<td>expiration</td>
								<td>string</td>
								<td>required expiration</td>
							</tr>
						</tbody>
					</table>
				</div>
				<h2 id="reference-logout">VoiceStorm.logout()</h2>
				<p>Logs out a currently logged in user. Returns a promise object. The success handler will always be called, but passed nothing.</p>
				<div className='data-table-responsive'>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.logout()</strong></td>
							</tr>
						</tbody>
					</table>
				</div>
				<h2 id="reference-post">VoiceStorm.post()</h2>
				<p>Requests a specific post. Returns a promise object. The success handler will be passed a <Link to="/api/Posts#get-post-id">GET /v1/post</Link> response.</p>
				<div className='data-table-responsive'>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.post(id)</strong></td>
							</tr>
							<tr>
								<td>id</td>
								<td>string</td>
								<td>required id of the post to request</td>
							</tr>
						</tbody>
					</table>
				</div>
				<h2 id="reference-postUrl">VoiceStorm.postUrl()</h2>
				<p>Given a <Link to="/api/Posts#get-post-id">GET /v1/post</Link> response, return a permalink to the post. <strong>This returns a string, not a promise object!<br className="Apple-interchange-newline" /></strong></p>
				<div className='data-table-responsive'>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.postUrl(post)</strong></td>
							</tr>
							<tr>
								<td>post</td>
								<td><Link to="/api/Posts#get-post-id">GET /v1/post</Link> response</td>
								<td>required post object to link to, eg, returned from <a href="#reference-post">VoiceStorm.post()</a></td>
							</tr>
						</tbody>
					</table>
				</div>
				<h2 id="reference-profileUrl">VoiceStorm.profileUrl()</h2>
				<p>Given a user id, return a permalink to the user's profile. <strong>This returns a string, not a promise object!<br className="Apple-interchange-newline" /></strong></p>
				<div className='data-table-responsive'>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.profileUrl(id)</strong></td>
							</tr>
							<tr>
								<td>id</td>
								<td>string</td>
								<td>required id of user to link to</td>
							</tr>
						</tbody>
					</table>
				</div>
				<h2 id="reference-putUser">VoiceStorm.putUser()</h2>
				<p>Update properties of a user. Returns a promise object. The success handler will be passed a <Link to="/api/Users#get-user">GET /v1/user</Link> response.</p>
				<div className='data-table-responsive'>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.putUser(user)</strong></td>
							</tr>
							<tr>
								<td>user</td>
								<td>object</td>
								<td>user is a set of key/value pairs to update. See <Link to="/api/Users#post-user-id">PUT /v1/user</Link> request.</td>
							</tr>
						</tbody>
					</table>
				</div>
				<h2 id="reference-reactToPost">VoiceStorm.reactToPost()</h2>
				<p>Shows the user interface to react to a specific post. Returns a promise object.</p>
				<p>The reactToPost method is a specialized version of <a href="#reference-sharePost">sharePost</a>.
It accepts all the same options as sharePost, plus an additional parameter specific to reactions.
</p>
				<p>See <a href="#reference-sharePost">sharePost</a> for a description of how to customize the
reactToPost user interface. All the same options on sharePost apply to reactToPost.</p>
				<div className='data-table-responsive'>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.reactToPost(id)</strong></td>
							</tr>
							<tr>
								<td>id</td>
								<td>string</td>
								<td>required ID of the post to react to</td>
							</tr>
						</tbody>
					</table>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.reactToPost(options)</strong></td>
							</tr>
							<tr>
								<td colSpan="3">options is a set of key/value pairs to configure the request:</td>
							</tr>
							<tr>
								<td>reactionType</td>
								<td>string</td>
								<td>required One of the <strong>reactionType</strong> values shown in <Link to="/api/Posts#post-post-id-reaction-reactiontype">{'POST /v1/post/{id}/reaction/{reactionType}'}</Link> reactionType request property.
Call <code>VoiceStorm.api('GET', 'providers')</code> to get a list of providers available in the community and the
reactionTypes they support. See <Link to="/api/Registration#get-providers">GET /v1/providers</Link> for more details.
</td>
							</tr>
							<tr>
								<td><em>sharePost options</em></td>
								<td></td>
								<td>All options supported by <a href="#reference-sharePost">sharePost</a> can also be passed to reactToPost.</td>
							</tr>
						</tbody>
					</table>
				</div>
				<h2 id="reference-reportPostViewEvent">VoiceStorm.reportPostViewEvent()</h2>
				<p>Records a post view in the reporting system. Use this on a single article view page to enable article view tracking. Returns a promise object. The success handler will be passed a <Link to="/api/Responses/successResponse">successResponse</Link>.</p>
				<div className='data-table-responsive'>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.reportPostViewEvent(id)</strong></td>
							</tr>
							<tr>
								<td>id</td>
								<td>string</td>
								<td>required id of the post to report on</td>
							</tr>
						</tbody>
					</table>
				</div>
				<h2 id="reference-sharePost">VoiceStorm.sharePost()</h2>
				<p>Shows the user interface to share a specific post. Returns a promise object. If the user is not currently logged on, the error handler will be called. By default, the share user interface is shown in a browser popup window. Because a popup window is shown, sharePost should only be called in response to a user click event; otherwise a user popup blocker may block the new window.</p>
				<p>To show the share user interface inside your own container, such as a <a href="http://getbootstrap.com/javascript/#modals" target="_blank" rel="noopener noreferrer">Bootstrap modal</a>, use the <strong>lightboxContainer</strong> option and handle sharePost events.</p>
				<div className='data-table-responsive'>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.sharePost(id)</strong></td>
							</tr>
							<tr>
								<td>id</td>
								<td>string</td>
								<td>required ID of the post to share</td>
							</tr>
						</tbody>
					</table>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.sharePost(options)</strong></td>
							</tr>
							<tr>
								<td colSpan="3">options is a set of key/value pairs to configure the request:</td>
							</tr>
							<tr>
								<td>postId</td>
								<td>string</td>
								<td>optional ID of the post to share. Either postId or postUrl must be set</td>
							</tr>
							<tr>
								<td>postUrl</td>
								<td>string</td>
								<td>optional URL to a page containing a post to share. Either postId or postUrl must be set</td>
							</tr>
							<tr>
								<td>lightboxContainer,<br />shareDialogLightboxContainer</td>
								<td>string</td>
								<td>optional jQuery selector for an element that will contain the sharing user interface instead of a browser popup window. This element is typically a &lt;div&gt; with no content.
sharePost will empty the contents of this element each time the sharing user interface is displayed. See <strong>Using a Lightbox</strong>, below,
for details. Although either option may be used, typically one passes lightboxContainer in the options object, or
sets <Link to="/javascript-sdk/environment#defaults">VoiceStorm.defaults</Link>.shareDialogLightboxContainer to apply this value globablly</td>
							</tr>
							<tr>
								<td>cssUrl,<br />shareDialogCssUrl</td>
								<td>string</td>
								<td>optional fully-qualifed (absolute) secure URL to a stylesheet to apply to the sharing user interface. The user interface always appears on a secure page, so this URL must also be secure (https) or it will be ignored. See <strong>Styling the User Interface</strong>, below, for details. Although either option may be used, typically one passes cssUrl in the options object, or sets <Link to="/javascript-sdk/environment#defaults">VoiceStorm.defaults</Link>.shareDialogCssUrl to apply this value globablly
</td>
							</tr>
						</tbody>
					</table>
				</div>
				<h3 id="reference-sharePost-lightbox">Using a Lightbox</h3>
				<p>By default, the sharing user interface opens in a browser popup window. You can change this behavior by setting <strong>lightboxContainer</strong> to an element on your page, such as a div inside a dialog, like a Bootstrap modal. Handle events sent by sharePost to show and hide your lightbox and update your page when a share takes place.</p>
				<h3 id="reference-sharePost-events">Lightbox Events</h3>
				<p>When <strong>lightboxContainer</strong> is set, sharePost will communicate to that element using jQuery events. Your event handlers will show and hide your lightbox and react to sharing actions as they occur. Each event is namespaced to the VoiceStorm Javascript SDK and sharePost method. Register your event handlers on the VoiceStorm jQuery object in case the VoiceStorm JavaScript SDK is using a different jQuery instance than your page.</p>
				<div className="data-table-responsive">
					<table className="table table-bordered table-striped js-table table-events">
						<tbody>
							<tr>
								<td colSpan="3"><strong>sharePost lightbox events<br /></strong></td>
							</tr>
							<tr>
								<td>show.voicestorm.sharepost</td>
								<td>This event fires just before the sharePost user interface is displayed. Use it to show your lightbox and display any type of loading indicator</td>
							</tr>
							<tr>
								<td>shown.voicestorm.sharepost</td>
								<td>This event fires when the sharing user interface is visible to the user. Use it to hide your loading indicator, if you displayed one during the show event</td>
							</tr>
							<tr>
								<td>authenticated.voicestorm.sharepost</td>
								<td>This event fires after a successful login from the sharing user interface.
The event contains an additional property called <code>login</code>.
Event.login contains the <code>token</code> and <code>expiration</code>
									returned in a <Link to="/api/Login#post-login">POST /v1/login</Link> response.
Use these properties to log the user into Dynamic Signal on your site by calling <a href="#reference-loginWithToken">VoiceStorm.loginWithToken()</a>.
</td>
							</tr>
							<tr>
								<td>shared.voicestorm.sharepost</td>
								<td style={{ whiteSpace: 'normal' }}>This event fires after a successful share. The ID of the post comment created for the share is available as the <code>detail</code>
									property of the event. Use that ID to get more information about the share. See example</td>
							</tr>
							<tr>
								<td>closed.voicestorm.sharepost</td>
								<td style={{ whiteSpace: 'normal' }}>This event fires after the user has dismissed the sharing user interface. Use it to hide your lightbox.</td>
							</tr>
						</tbody>
					</table>
				</div>
				<h3>Lightbox Example</h3>
				<h5>HTML</h5>
				<pre className="language-markup"><code className="language-markup">{`<!-- Sharing Bootstrap Modal -->
<div id="share-modal" class="modal fade" tabindex="-1" role="dialog" aria-hidden="true">
	<div class="modal-dialog">
		<div class="modal-content">
			<div class="modal-header">
				<button type="button" class="close" data-dismiss="modal" aria-hidden="true">&times;</button>
				<h4 class="modal-title" id="myModalLabel">Share a Post</h4>
			</div>
			<h4 id="share-modal-loading" style="display: none; margin-left: 20px;"><span class="glyphicon glyphicon-refresh"></span> Loading... </h4>
			<div id="share-modal-sharepost-container" class="modal-body">
			</div>
		</div>
	</div>
</div>`}</code></pre>
				<h5>Script</h5>
				<pre className="language-javascript"><code className="language-javascript">{`// Tell VoiceStorm JavaScript SDK to use my modal lightbox
VoiceStorm.defaults.shareDialogLightboxContainer = '#share-modal-sharepost-container';

// Handle VoiceStorm.sharePost lightbox events
VoiceStorm.jQuery('#share-modal-sharepost-container')
	.on('show.voicestorm.sharepost', function (e) {
		$('#share-modal-sharepost-container').empty();
		$('#share-modal-loading').show();
		$('#share-modal').modal('show');
	})
	.on('shown.voicestorm.sharepost', function (e) {
		$('#share-modal-loading').hide();
	})
	.on('authenticated.voicestorm.sharepost', function (e) {
		VoiceStorm.loginWithToken(e.login.token, e.login.expiration);
	})
	.on('shared.voicestorm.sharepost', function (e) {
		var id = e.detail;
		VoiceStorm.api('GET', 'postcomment/' + id).then(function (postcomment) {
			// Update page with new post comment
		});
	})
	.on('closed.voicestorm.sharepost', function (e) {
		$('#share-modal').modal('hide');
	});`}</code></pre>
				<h3 id="reference-sharePost-cssurl">Styling the User Interface</h3>
				<p>
					The share dialog uses CSS classes for styling.
					The root class is <code>voicestorm-share-dialog</code>.
					To override these classes in your own stylesheet, set the <code>shareDialogCssUrl</code> property in <Link to="/javascript-sdk/environment#defaults">VoiceStorm.defaults</Link> or <code>cssUrl</code> in the options object passed to <strong>VoiceStorm.sharePost</strong>.
					See <a href="#reference-sharePost">VoiceStorm.sharePost()</a> above for details.
				</p>
				<h2 id="reference-socialLogin">VoiceStorm.socialLogin()</h2>
				<p>Shows the user interface to login via a specific social provider. Returns a promise object. The success handler will be passed a <Link to="/api/Users#get-user">GET /v1/user</Link> response representing the newly logged in user. The VoiceStorm SDK will persist the logged in user's session until it expires or <a href="#reference-logout">VoiceStorm.logout()</a> is called. In most cases, this will show a 3rd party Oauth dialog as a popup window. Because a popup window is required, this should only be called in response to a user click event; otherwise a user popup blocker may block the new window.</p>
				<p>For this function to succeed, you must include a channel.html file on your site and set <code>VoiceStorm.defaults.channelUrl</code> to point to that file. See <a href="#social"><strong>Social Login and Channel Registration</strong></a> for details.</p>
				<div className='data-table-responsive'>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.socialLogin(providerName)</strong></td>
							</tr>
							<tr>
								<td>providerName</td>
								<td>string</td>
								<td>
									canonicalName of a social provider that supports login, such as "Facebook". To get a list of social providers that support login,
								call <a href="#reference-socialProviders"><strong>VoiceStorm.socialProviders('LoginRegistration')</strong></a>. providerName is case-sensitive
							</td>
							</tr>
						</tbody>
					</table>
				</div>
				<h2 id="reference-socialProviders">VoiceStorm.socialProviders()</h2>
				<p>Requests a list of available social providers. Returns a promise object. The success handler will be passed a <Link to="/api/Registration#get-providers">GET /v1/providers</Link> response.</p>
				<div className='data-table-responsive'>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td><strong>VoiceStorm.socialProviders()</strong></td>
							</tr>
							<tr>
								<td>returns all social providers a user can add</td>
							</tr>
						</tbody>
					</table>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.socialProviders(type)</strong></td>
							</tr>
							<tr>
								<td>type</td>
								<td>string</td>
								<td>one of the following:<br />
									<ul>
										<li>'AddChannel' to return all social providers a user can add</li>
										<li>'LoginRegistration' to return only providers enabled for login and registration</li>
									</ul>
								</td>
							</tr>
						</tbody>
					</table>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.socialProviders(type)</strong></td>
							</tr>
							<tr>
								<td>type</td>
								<td>bool</td>
								<td>true for all social providers a user can add, false for only login and registration providers</td>
							</tr>
						</tbody>
					</table>
				</div>
				<h2 id="reference-stream">VoiceStorm.stream()</h2>
				<p>Requests the posts in a specific stream. Returns a promise object. The success handler will be passed a <Link to="/api/Streams#get-stream">GET /v1/stream</Link> response.</p>
				<div className='data-table-responsive'>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.stream(name)</strong></td>
							</tr>
							<tr>
								<td>name</td>
								<td>string</td>
								<td>required name of the stream to request posts for</td>
							</tr>
						</tbody>
					</table>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.stream(options)</strong></td>
							</tr>
							<tr>
								<td colSpan="3">options is a set of key/value pairs to configure the request:</td>
							</tr>
							<tr>
								<td>name</td>
								<td>string</td>
								<td>required name of the stream to request posts for</td>
							</tr>
							<tr>
								<td>skip</td>
								<td>numbe</td>
								<td>optional start index of posts in stream</td>
							</tr>
							<tr>
								<td>take</td>
								<td>number</td>
								<td>optional count of posts to return</td>
							</tr>
							<tr>
								<td>include</td>
								<td>string</td>
								<td>optional comma delimited string of the following to request additional information:<br />
									<ul>
										<li>&lsquo;content&rsquo; to return the full post content</li>
										<li>&lsquo;images&rsquo; to return the post&rsquo;s images</li>
									</ul>
								</td>
							</tr>
							<tr>
								<td>idsOnly</td>
								<td>bool</td>
								<td>optionally true to only return post ids</td>
							</tr>
						</tbody>
					</table>
				</div>
				<h2 id="reference-updateUserNotificationPreferences">VoiceStorm.updateUserNotificationPreferences()</h2>
				<p>Update a user's notification preferences. Returns a promise object. The success handler will be passed a <Link to="/api/Responses/successResponse">successResponse</Link>.</p>
				<div className='data-table-responsive'>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.updateUserNotificationPreferences(preferences)</strong></td>
							</tr>
							<tr>
								<td>preferences</td>
								<td>object</td>
								<td>preferences is a set of key/value pairs to update. See the <Link to="/api/Users#get-user-id-notifications-preferences">{'/v1/user/{id}/notifications/preferences'}</Link> response.</td>
							</tr>
						</tbody>
					</table>
				</div>
				<h2 id="reference-user">VoiceStorm.user()</h2>
				<p>Requests details of a specific user. Returns a promise object. The success handler will be passed a <Link to="/api/Users#get-user">GET /v1/user</Link> response.</p>
				<div className='data-table-responsive'>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td><strong>VoiceStorm.user()</strong></td>
							</tr>
							<tr>
								<td>request details of the currently logged in user</td>
							</tr>
						</tbody>
					</table>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.user(id)</strong></td>
							</tr>
							<tr>
								<td>id</td>
								<td>number</td>
								<td>required id of user to request</td>
							</tr>
						</tbody>
					</table>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.user(options)</strong></td>
							</tr>
							<tr>
								<td colSpan="3">options is a set of key/value pairs to configure the request:</td>
							</tr>
							<tr>
								<td>id</td>
								<td>number</td>
								<td>optional id of user to request, defaults to currently logged in user</td>
							</tr>
							<tr>
								<td>include</td>
								<td>string</td>
								<td>optional comma delimited string of the following to request additional user information:<br />
									<ul>
										<li>&lsquo;address&rsquo; to include the user&rsquo;s address</li>
										<li>&lsquo;affiliations&rsquo; to include the user affiliations</li>
										<li>&lsquo;channels&rsquo; to include connected social providers</li>
										<li>&lsquo;images&rsquo; to include the profile image</li>
									</ul>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<h2 id="reference-userAddChannel">VoiceStorm.userAddChannel()</h2>
				<p>Shows the user interface for a user to add a specific social provider account to their profile. Returns a promise object. The success handler will be passed a <Link to="/api/responses/providerAuthResponse">providerAuthResponse</Link>. In most cases, this will show a 3rd party Oauth dialog as a popup window. Because a popup window is required, this should only be called in response to a user click event; otherwise a user popup blocker may block the new window.</p>
				<p>For this function to succeed, you must include a channel.html file on your site and set <code>VoiceStorm.defaults.channelUrl</code> to point to that file. See <Link to="/javascript-sdk/auth#social"><strong>Social Login and Channel Registration</strong></Link> for details.</p>
				<div className='data-table-responsive'>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.userAddChannel(providerName)</strong></td>
							</tr>
							<tr>
								<td>providerName</td>
								<td>string</td>
								<td>canonicalName of a social provider that supports add channel, such as "Facebook". To get a list of social providers that support add channel, call <a href="#reference-socialProviders"><strong>VoiceStorm.socialProviders('AddChannel')</strong></a>. providerName is case-sensitive</td>
							</tr>
						</tbody>
					</table>
				</div>
				<h2 id="reference-userNotificationPreferences">VoiceStorm.userNotificationPreferences()</h2>
				<p>Requests a user's notification preferences. Returns a promise object. The success handler will be passed a <Link to="/api/Users#get-user-id-notifications-preferences">{'GET /v1/user/{id}/notifications/preferences'}</Link>.</p>
				<div className='data-table-responsive'>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.userNotificationPreferences(id)</strong></td>
							</tr>
							<tr>
								<td>id</td>
								<td>number</td>
								<td>required id of user to request</td>
							</tr>
						</tbody>
					</table>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.<strong>userNotificationPreferences</strong>(options)</strong></td>
							</tr>
							<tr>
								<td colSpan="3">options is a set of key/value pairs to configure the request:</td>
							</tr>
							<tr>
								<td>id</td>
								<td>number</td>
								<td>required id of user to request</td>
							</tr>
						</tbody>
					</table>
				</div>
				<h2 id="reference-userNotificationsResendSmsCode">VoiceStorm.userNotificationsResendSmsCode()</h2>
				<p>Returns a promise object. The success handler will be passed a <Link to="/api/Responses/successResponse">successResponse</Link>.</p>
				<div className='data-table-responsive'>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.userNotificationsResendSmsCode(id, returnUrl)</strong></td>
							</tr>
							<tr>
								<td>id</td>
								<td>number</td>
								<td>required user id</td>
							</tr>
							<tr>
								<td>returnUrl</td>
								<td>string</td>
								<td>required URL in the sms verification message</td>
							</tr>
						</tbody>
					</table>
				</div>
				<h2 id="reference-userNotificationsVerifySmsCode">VoiceStorm.userNotificationsVerifySmsCode()</h2>
				<p>Returns a promise object. The success handler will be passed a <Link to="/api/Responses/successResponse">successResponse</Link>.</p>
				<div className='data-table-responsive'>
					<table className="table table-bordered table-striped js-table">
						<tbody>
							<tr>
								<td colSpan="3"><strong>VoiceStorm.userNotificationsVerifySmsCode(id, smsVerificationCode)</strong></td>
							</tr>
							<tr>
								<td>id</td>
								<td>number</td>
								<td>required user id</td>
							</tr>
							<tr>
								<td>smsVerificationCode</td>
								<td>string</td>
								<td>required sms verification code</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		);
	}
}

export default Reference;



// WEBPACK FOOTER //
// ./src/components/javascript-sdk/Reference.js